import { Box, Grid } from "@mui/material";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { Api } from "../../api/Api";
import { thunkCreateErrorNotification } from "../../store/actions/NotificationActions";
import { IStore } from "../../store/IStore";
import { BonusRuleDto } from "../../store/models/bonusRule/BonusRuleDto";
import { BonusRuleStatus } from "../../store/models/bonusRule/BonusRuleStatus";
import { BonusRuleType } from "../../store/models/bonusRule/BonusRuleType";
import { BonusRuleValueType } from "../../store/models/bonusRule/BonusRuleValueType";
import { RewardType } from "../../store/models/bonusRule/RewardType";
import { OperationalUnitDto } from "../../store/models/operationalUnit/OperationalUnitDto";
import { DateTag } from "../atoms/DateTag";
import { COLOR_GRAY_3, COLOR_GRAY_4 } from "../atoms/ImsMaterialTheme";
import { getInteractionTypeOptions } from "./BonusRuleForm";
import BonusRuleMenu from "./BonusRuleMenu";
import { BonusSearchCondition } from "./BonusRulePage";

export function getUnitNames(unitNumbers: string[], operationalUnits: OperationalUnitDto[]): string {
  const unitNames = operationalUnits
    .filter((unit) => unitNumbers.includes(unit.unitNumber))
    .slice(0, 5)
    .map((unit) => unit.unitName);

  if (unitNames.length > 0) {
    return unitNames.join(", ") + (unitNumbers.length > 5 ? "..." : "");
  }

  return "";
}

interface BonusRuleTableProps {
  operationalUnits(unitNumbers: any, operationalUnits: any): unknown;
  searchCondition: BonusSearchCondition;
}

const BonusTable = (props: BonusRuleTableProps & ThunkProps) => {
  const { t } = useTranslation();

  const interactionTypeOptions = getInteractionTypeOptions(t);

  function getCustomerInteractionDescription(row: BonusRuleDto): string {
    if (row.type === BonusRuleType.TRANSACTION) {
      return t("bonusRuleTable.purchase");
    }
    const interactionTypeOption = interactionTypeOptions.find(
      (interactionTypeOption) => interactionTypeOption.value === row.customerInteractionType
    );
    return interactionTypeOption?.label || t("bonusRuleTable.ownActivity");
  }

  function getValueTypeDescription(valueType: BonusRuleValueType): string {
    const valueDescriptions = {
      [BonusRuleValueType.FIX]: "",
      [BonusRuleValueType.FACTOR]: t("bonusRuleTable.factor"),
      [BonusRuleValueType.PER_CENT]: t("bonusRuleTable.perCent"),
    };
    return valueDescriptions[valueType] || "";
  }

  function getRewardTypeDescription(rewardType?: RewardType): string {
    if (!rewardType) return t("bonusRuleTable.rewardTypeBonusPoint"); // default to bonus point
    
    const rewardDescriptions = {
      [RewardType.BONUS_POINT]: t("bonusRuleTable.rewardTypeBonusPoint"),
      [RewardType.STAMP_CARD]: t("bonusRuleTable.rewardTypeStampCard"),
    };
    return rewardDescriptions[rewardType] || t("bonusRuleTable.rewardTypeBonusPoint");
  }

  const { searchCondition, bonusRules, operationalUnits, thunkCreateErrorNotification } = props;
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "name", sort: "asc" }]);
  const [rows, setRows] = useState<BonusRuleDto[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => setPage(0), [searchCondition]);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    Api.getBonusRulePage({
      page: page + 1,
      size,
      sortBy: sortModel[0]?.field,
      sortDirection: sortModel[0]?.sort ?? undefined,
      name: searchCondition.name || undefined,
      status: searchCondition.showInactive ? undefined : BonusRuleStatus.ENABLED,
      running: searchCondition.isRunning || undefined,
      unitNumbers: searchCondition.unitNumbers?.length ? searchCondition.unitNumbers : undefined,
    })
      .then((result) => {
        if (active) {
          setRows(result.data);
          setTotal(result.total);
        }
      })
      .catch((e) => active && thunkCreateErrorNotification(t("bonusRuleTable.errorLoading"), e))
      .finally(() => active && setIsLoading(false));
    return () => {
      active = false;
    };
  }, [searchCondition, page, size, sortModel, bonusRules, thunkCreateErrorNotification, t]);

  const columns: GridColDef<BonusRuleDto>[] = [
    {
      field: "name",
      headerName: t("bonusRuleTable.name"),
      flex: 1,
      renderCell: ({ row }) => <b>{row.ruleName}</b>,
    },
    {
      field: "validFrom",
      headerName: t("bonusRuleTable.execution"),
      flex: 1,
      renderCell: ({ row }) => (
        <>
          {!row.validTill ? t("bonusRuleTable.from") : ""} <DateTag date={row.validFrom} />
          {row.validTill ? t("bonusRuleTable.to") : ""} <DateTag date={row.validTill} />
        </>
      ),
    },
    {
      field: "unitNumbers",
      headerName: t("bonusRuleTable.operationalUnit"),
      flex: 1,
      sortable: false,
      valueGetter: ({ row }) => getUnitNames(row.unitNumbers, operationalUnits),
    },
    {
      field: "value",
      headerName: t("bonusRuleTable.bonusPoints"),
      flex: 1,
      sortable: false,
      valueGetter: ({ row }) => Number(row.value) + getValueTypeDescription(row.valueType),
    },
    {
      field: "rewardType",
      headerName: t("bonusRuleTable.rewardType"),
      flex: 1,
      sortable: false,
      valueGetter: ({ row }) => getRewardTypeDescription(row.rewardType),
    },
    {
      field: "type",
      headerName: t("bonusRuleTable.activity"),
      flex: 1,
      sortable: false,
      valueGetter: ({ row }) => getCustomerInteractionDescription(row),
    },
    {
      field: "status",
      headerName: t("bonusRuleTable.status"),
      flex: 1,
      sortable: false,
      valueGetter: ({ row }) =>
        row.status === BonusRuleStatus.ENABLED ? t("bonusRuleTable.active") : t("bonusRuleTable.inactive"),
    },
    {
      field: "action",
      headerName: "",
      width: 50,
      sortable: false,
      renderCell: ({ row }) => <BonusRuleMenu bonusRule={row} />,
    },
  ];

  return (
    <Grid>
      <Box
        sx={{
          width: "100%",
          bgcolor: "white",
          boxShadow: 2,
          borderRadius: 2,
          "& .MuiDataGrid-columnHeaders": { backgroundColor: COLOR_GRAY_3 },
          "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
          "& .disabled-rule": { color: COLOR_GRAY_4 },
        }}
      >
        <DataGrid
          disableColumnMenu
          disableColumnSelector
          disableSelectionOnClick
          disableVirtualization
          autoHeight
          getRowId={(row) => row.ruleNumber}
          getRowClassName={({ row }) => (row.status === BonusRuleStatus.DISABLED ? "disabled-rule" : "")}
          rows={rows}
          columns={columns}
          loading={isLoading}
          rowCount={total}
          pagination
          paginationMode="server"
          page={page}
          pageSize={size}
          rowsPerPageOptions={[10, 50, 100]}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          componentsProps={{ pagination: { labelRowsPerPage: t("common.pagination.rowsPerPage") } }}
        />
      </Box>
    </Grid>
  );
};

const mapStateToProps = (state: IStore) => ({
  bonusRules: state.bonusRules.bonusRules,
  operationalUnits: state.operationalUnits.operationalUnits,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({ thunkCreateErrorNotification }, dispatch);

const connector = connect(mapStateToProps, mapDispatchToProps);
type ThunkProps = ConnectedProps<typeof connector>;
export default connector(BonusTable);
